
'use client';

import { Button } from '@/components/ui/button';
import { ArrowRight, Menu, X } from 'lucide-react';
import { useState } from 'react';

const Squiggle = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 81 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M1 10.2914C12.4286 1.94412 35.8286 -1.2721 45.4286 2.37332C55.0286 6.01874 58.5714 13.9133 69.4286 10.2914C80.2857 6.66953 80.2857 1.15783 80.2857 1.15783"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IllustrationPlaceholder = ({
  className,
  seed,
}: {
  className?: string;
  seed: string;
}) => (
  <div
    className={`bg-gray-200 rounded-lg flex items-center justify-center ${className}`}
  >
    <img
      src={`https://api.dicebear.com/8.x/bottts-neutral/svg?seed=${seed}&radius=15&backgroundType=gradientLinear&scale=80&translateX=-5`}
      alt="illustration"
      className="w-full h-full"
    />
  </div>
);

export function PlayfulIllustrationsHomepage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <div className="bg-[#FEF9F0] font-sans">
      <style jsx global>{`
        @import url('https://fonts.googleapis.com/css2?family=Paytone+One&display=swap');
        .font-display { font-family: 'Paytone One', sans-serif; }
      `}</style>
      <header className="container mx-auto px-8 py-6 flex justify-between items-center">
        <h2 className="font-display text-3xl text-gray-800">Doodlebug</h2>
        <nav className="hidden md:flex items-center gap-8">
          <a
            href="#"
            className="font-semibold text-gray-600 hover:text-blue-500"
          >
            How it works
          </a>
          <a
            href="#"
            className="font-semibold text-gray-600 hover:text-blue-500"
          >
            Pricing
          </a>
          <Button className="bg-gray-800 text-white rounded-full hover:bg-gray-700">
            Get Started
          </Button>
        </nav>
        <Button variant="ghost" className="md:hidden" onClick={() => setIsMenuOpen(true)}>
          <Menu />
        </Button>
      </header>

      {isMenuOpen && (
        <div className="fixed inset-0 bg-[#FEF9F0] z-50 p-8 md:hidden">
             <div className="flex justify-between items-center">
                <h2 className="font-display text-3xl text-gray-800">Doodlebug</h2>
                <Button variant="ghost" onClick={() => setIsMenuOpen(false)}>
                    <X/>
                </Button>
            </div>
            <nav className="flex flex-col items-start gap-6 mt-12 text-2xl">
                 <a href="#" className="font-semibold text-gray-600 hover:text-blue-500">How it works</a>
                 <a href="#" className="font-semibold text-gray-600 hover:text-blue-500">Pricing</a>
                 <Button className="bg-gray-800 text-white rounded-full hover:bg-gray-700 w-full mt-4 text-lg">Get Started</Button>
            </nav>
        </div>
      )}


      <main>
        <section className="container mx-auto px-8 py-20 text-center">
          <h1 className="font-display text-5xl md:text-7xl text-gray-900 leading-tight">
            Teamwork made <span className="text-yellow-500">fun</span> and{' '}
            <span className="text-pink-500">easy</span>
          </h1>
          <div className="relative inline-block mt-2">
            <Squiggle className="w-full stroke-blue-500" />
          </div>
          <p className="mt-8 max-w-2xl mx-auto text-lg text-gray-600">
            Doodlebug is the all-in-one collaborative whiteboard where your
            team can bring ideas to life.
          </p>
          <div className="mt-10">
            <Button
              size="lg"
              className="bg-blue-500 text-white rounded-full hover:bg-blue-600 px-8"
            >
              Start your free trial <ArrowRight className="ml-2" />
            </Button>
          </div>
        </section>

        <section className="px-8 pb-20">
          <div className="relative h-[40vh] md:h-[60vh] max-w-6xl mx-auto">
            <div className="absolute w-2/3 h-full bg-pink-200 rounded-2xl top-0 left-0 -rotate-3"></div>
            <div className="absolute w-2/3 h-full bg-yellow-200 rounded-2xl top-0 right-0 rotate-2"></div>
            <div className="absolute w-full h-full p-4 md:p-8">
              <div className="bg-white rounded-2xl shadow-xl w-full h-full flex items-center justify-center p-4 md:p-8">
                <IllustrationPlaceholder
                  seed="main-doodle"
                  className="w-full h-full"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 bg-white px-4">
          <div className="container mx-auto">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="font-display text-4xl text-gray-800">
                A better way to brainstorm
              </h2>
              <p className="mt-4 text-gray-600">
                From sticky notes to flowcharts, we've got you covered.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-12">
              <div className="text-center">
                <IllustrationPlaceholder
                  seed="feature1"
                  className="w-48 h-48 mx-auto rounded-full mb-6"
                />
                <h3 className="font-display text-2xl text-gray-800">
                  Infinite Canvas
                </h3>
                <p className="mt-2 text-gray-500">
                  Never run out of room for your big ideas. Pan, zoom, and
                  explore your creations.
                </p>
              </div>
              <div className="text-center">
                <IllustrationPlaceholder
                  seed="feature2"
                  className="w-48 h-48 mx-auto rounded-full mb-6"
                />
                <h3 className="font-display text-2xl text-gray-800">
                  Real-time Cursors
                </h3>
                <p className="mt-2 text-gray-500">
                  See your teammates' cursors dance around the board as you
                  work together.
                </p>
              </div>
              <div className="text-center">
                <IllustrationPlaceholder
                  seed="feature3"
                  className="w-48 h-48 mx-auto rounded-full mb-6"
                />
                <h3 className="font-display text-2xl text-gray-800">
                  Templates Galore
                </h3>
                <p className="mt-2 text-gray-500">
                  Kickstart your session with pre-built templates for any
                  project.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-yellow-400 py-20 text-center px-4">
          <div className="container mx-auto">
            <h2 className="font-display text-4xl text-gray-900">
              Ready to start doodling?
            </h2>
            <p className="mt-4 text-gray-700 max-w-xl mx-auto">
              Get your whole team on board for free. No credit card required!
            </p>
            <Button
              size="lg"
              className="mt-8 bg-gray-800 text-white rounded-full hover:bg-black px-8"
            >
              Sign Up Free
            </Button>
          </div>
        </section>
      </main>
      <footer className="bg-[#FEF9F0] py-12 px-4">
        <div className="container mx-auto text-center text-gray-500">
          <p className="font-semibold text-gray-700 mb-4 font-display text-xl">
            Doodlebug
          </p>
          <div className="flex justify-center gap-6 mb-8">
            <a href="#" className="hover:text-blue-500">
              Twitter
            </a>
            <a href="#" className="hover:text-blue-500">
              Contact
            </a>
            <a href="#" className="hover:text-blue-500">
              Privacy
            </a>
          </div>
          <p className="text-sm">
            &copy; {new Date().getFullYear()} Doodlebug Inc. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
